import {useAnalytics} from '@github-ui/use-analytics'
import {TriangleDownIcon} from '@primer/octicons-react'
import {Button, NavList} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef, useState} from 'react'

import type {SidebarLink} from '../types/sidebar-types'
import {getHandlerNavItemClick} from '../utils/sidebar-link'
import styles from './ListSidebar.module.css'
import {SidebarLinkIcon} from './SidebarLinkIcon'

interface ListSidebarProps {
  links: SidebarLink[]
  onQueryChanged: (query: string) => void
  selectedLink: string
}

export function ListSidebar({links, selectedLink, onQueryChanged}: ListSidebarProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  return (
    <NavList>
      {links.map(({id, query, title, icon, color}) => (
        <NavList.Item
          aria-current={selectedLink === id && 'page'}
          key={id}
          href={`?q=${query}`}
          onClick={getHandlerNavItemClick({onQueryChanged, sendAnalyticsEvent, query, id})}
        >
          <NavList.LeadingVisual>
            <SidebarLinkIcon name={icon} color={color} />
          </NavList.LeadingVisual>
          {title}
        </NavList.Item>
      ))}
    </NavList>
  )
}

export function ListSidebarCollapsedButton({onQueryChanged, ...props}: ListSidebarProps) {
  const [isDialogOpen, showDialog] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const selectedLinkItem = props.links.find(link => link.id === props.selectedLink)
  const listTitle = selectedLinkItem?.title || 'Search'

  return (
    <>
      <Button
        ref={buttonRef}
        variant="invisible"
        onClick={() => showDialog(true)}
        trailingVisual={TriangleDownIcon}
        aria-describedby="types-collapsed-button-description"
        className={styles.sidebarToggleButton}
      >
        <h1 className={styles.sidebarHeading}>{listTitle}</h1>
      </Button>
      <span className="sr-only" id="types-collapsed-button-description" aria-live="polite">
        Change the repository type filter
      </span>
      {isDialogOpen && (
        <Dialog onClose={() => showDialog(false)} title="Types" width="small" returnFocusRef={buttonRef}>
          <ListSidebar
            onQueryChanged={q => {
              onQueryChanged(q)
              showDialog(false)
            }}
            {...props}
          />
        </Dialog>
      )}
    </>
  )
}

try{ ListSidebar.displayName ||= 'ListSidebar' } catch {}
try{ ListSidebarCollapsedButton.displayName ||= 'ListSidebarCollapsedButton' } catch {}